/**
 * Parsowanie i budowa iCal (VEVENT, RRULE) dla harmonogramów dostępu / rezerwacji.
 */
import { DateAdapter, RRule, VEvent } from 'config/rschedule'
import { addDays } from 'date-fns'
import {
  AccessScheduleType,
  DEFAULT_TIME_ZONE,
  Frequency,
  WEEK_DAYS,
  alignRangeToFirstWeekday,
  combineDateAndTime,
  getRecurringScheduleDurationMs,
  getReferenceScheduleDates,
  normalizeScheduleFormDates,
  resolveScheduleAllDayFromIcal,
  sortWeekdays,
  weekdaysBetweenDates,
  weekdaysFromWeeklyRecurringRange,
} from './schedule'

/** Sam kod dnia w BYDAY (np. MO). */
const WEEKDAY_ONLY_BYDAY_PATTERN = new RegExp(`^(${WEEK_DAYS.join('|')})$`)

/** Kod z numerem pozycji (np. -1FR) — poza obsługą UI. */
const WEEKDAY_WITH_POSITION_BYDAY_PATTERN = new RegExp(
  `^(-?\\d+)(${WEEK_DAYS.join('|')})$`
)

/** Skleja złożone linie iCal (łamanie + kontynuacja ze spacją). */
export const flattenIcalLines = (icalText: string): string =>
  icalText.replace(/\r\n/g, '\n').replace(/\n[ \t]/g, '')

/** Usuwa TZID z DTSTART/DTEND, żeby parser biblioteki mógł sparsować czas. */
export const removeTimezoneFromIcalDates = (icalText: string): string =>
  flattenIcalLines(icalText)
    .split('\n')
    .map(line => {
      const trimmedLine = line.trim()
      return /^DT(START|END);TZID=/i.test(trimmedLine)
        ? trimmedLine.replace(
            /^DT(START|END);TZID=(?:[^:\n"]+|"[^"]*"):/i,
            'DT$1:'
          )
        : trimmedLine
    })
    .join('\n')

/** Wyciąga kody dni z RRULE BYDAY (tylko MO…SU, bez pozycji typu -1FR). */
export const weekdayCodesFromIcal = (
  icalText: string | null | undefined
): string[] | null => {
  if (!icalText) return null

  const unfolded = flattenIcalLines(icalText)
  const byDayMatch = unfolded.match(/(?:^|\n)RRULE:[^\n]*?BYDAY=([^;\r\n]+)/im)
  if (!byDayMatch?.[1]) return null

  const weekdayCodes: string[] = []
  for (const token of byDayMatch[1].split(',')) {
    const tokenUpper = token.trim().toUpperCase()
    const positionMatch = tokenUpper.match(/^(-?\d+)([A-Z]{2})$/)
    const code = positionMatch ? positionMatch[2] : tokenUpper.slice(0, 2)
    if (WEEK_DAYS.includes(code)) weekdayCodes.push(code)
  }

  return weekdayCodes.length
    ? sortWeekdays(Array.from(new Set(weekdayCodes)))
    : null
}

/**
 * Czy VEVENT z RRULE mieści się w UI (FREQ DAILY/WEEKLY, proste BYDAY, bez EXDATE/RDATE).
 * Pusty lub bez RRULE = obsługiwany.
 */
export const isRecurringPatternSupportedInUi = (
  icalText: string | null | undefined
): boolean => {
  if (!icalText?.trim()) return true

  const unfolded = flattenIcalLines(icalText)
  const rruleLines = unfolded.match(/(?:^|\n)RRULE:[^\n]+/gi)
  if (!rruleLines) return true
  if (rruleLines.length > 1) return false

  const rruleBody = rruleLines[0].replace(/^RRULE:/i, '').toUpperCase()
  const frequencyMatch = rruleBody.match(/\bFREQ=([A-Z]+)/)
  if (!frequencyMatch) return false

  const recurrenceFrequency = frequencyMatch[1]
  if (recurrenceFrequency !== 'DAILY' && recurrenceFrequency !== 'WEEKLY') {
    return false
  }

  if (
    /\bBY(MONTH|MONTHDAY|YEARDAY|WEEKNO|SETPOS|HOUR|MINUTE|SECOND)=/.test(
      rruleBody
    )
  ) {
    return false
  }

  if (recurrenceFrequency === 'DAILY' && /\bBYDAY=/.test(rruleBody))
    return false

  const byDayMatch = rruleBody.match(/\bBYDAY=([^;\r\n]+)/)
  if (byDayMatch?.[1]) {
    for (const token of byDayMatch[1].split(',')) {
      const tokenUpper = token.trim().toUpperCase()
      if (!tokenUpper) return false
      if (WEEKDAY_WITH_POSITION_BYDAY_PATTERN.test(tokenUpper)) return false
      if (!WEEKDAY_ONLY_BYDAY_PATTERN.test(tokenUpper)) return false
    }
  }

  if (/(?:^|\n)(EXDATE|RDATE|RECURRENCE-ID)[;:]/i.test(unfolded)) return false

  return true
}

/** Usuwa opakowanie VCALENDAR/VEVENT — zostają same linie pól. */
function removeIcalWrappers(icalText: string): string {
  return icalText
    .replace(/\r\n/g, '\n')
    .replace(/^\s*BEGIN:VEVENT\s*$/gim, '')
    .replace(/^\s*END:VEVENT\s*$/gim, '')
    .replace(/^\s*BEGIN:VCALENDAR\s*$/gim, '')
    .replace(/^\s*END:VCALENDAR\s*$/gim, '')
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .join('\n')
}

/** Escapuje wartość TZID (cudzysłowy przy znakach specjalnych). */
function escapeIcalTimezoneId(timezoneId: string): string {
  if (/[",;\\]/.test(timezoneId)) {
    return `"${timezoneId.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
  }
  return timezoneId
}

/** Wartość daty/czasu z linii DTSTART lub DTEND. */
function datetimeFromIcalLine(line: string): string {
  const match = line.match(/^(?:DTSTART|DTEND)[^:]*:(.+)$/i)
  if (!match?.[1]) return ''
  return match[1].split(';')[0].trim().replace(/Z$/i, '')
}

/** Zaokrągla sekundy i milisekundy przy budowaniu VEVENT. */
function roundToWholeMinutes(date: Date) {
  const rounded = new Date(date)
  rounded.setSeconds(0, 0)
  return rounded
}

/** Nakłada TZID na DTSTART/DTEND i czyści Z z UNTIL przed zapisem. */
function applyTimezoneToIcalExport(
  icalText: string,
  timeZoneId: string | null
) {
  const timezoneParam =
    timeZoneId != null && timeZoneId.trim() !== ''
      ? escapeIcalTimezoneId(timeZoneId.trim())
      : null

  let lines = removeIcalWrappers(icalText)
    .split(/\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0)

  lines = lines.map(line => {
    const upperLine = line.toUpperCase()
    if (upperLine.startsWith('DTSTART')) {
      const value = datetimeFromIcalLine(line)
      if (!value) return line
      return timezoneParam
        ? `DTSTART;TZID=${timezoneParam}:${value}`
        : `DTSTART:${value}`
    }
    if (upperLine.startsWith('DTEND')) {
      const value = datetimeFromIcalLine(line)
      if (!value) return line
      return timezoneParam
        ? `DTEND;TZID=${timezoneParam}:${value}`
        : `DTEND:${value}`
    }
    if (/UNTIL=/i.test(line)) {
      return line.replace(/UNTIL=([^;\n]+)/i, (_, untilValue) => {
        return `UNTIL=${untilValue.replace(/Z$/i, '')}`
      })
    }
    return line
  })

  return lines.join('\n')
}

/** Pola harmonogramu potrzebne do zbudowania RRULE / VEVENT. */
export interface ScheduleIcalFormData {
  scheduleType: AccessScheduleType
  temporaryFromDate: Date | null
  temporaryToDate: Date | null
  scheduleFromDate: Date | null
  scheduleToDate: Date | null
  repeatUntil: Date | null
  frequency: Frequency
  daysOfWeek: string[]
  scheduleAllDay: boolean
}

/** Stan formularza po parsowaniu iCal. */
export type ParsedScheduleFromIcal = ScheduleIcalFormData & {
  recurringPatternLoadUnsupported: boolean
}

/** Opcje eksportu iCal (strefa na liniach DTSTART/DTEND). */
export type IcalExportOptions = {
  timeZoneId: string | null
}

/** Buduje iCal z pól harmonogramu (tymczasowy lub cykliczny). */
export function scheduleFormToIcal(
  {
    scheduleType,
    temporaryFromDate,
    temporaryToDate,
    scheduleFromDate,
    scheduleToDate,
    repeatUntil,
    frequency,
    daysOfWeek,
    scheduleAllDay = false,
  }: ScheduleIcalFormData,
  { timeZoneId }: IcalExportOptions
): string | null {
  let event: VEvent | undefined
  let fromDate: Date | null = null
  let toDate: Date | null = null

  const isTemporary = scheduleType === AccessScheduleType.Temporary
  const isRecurring = scheduleType === AccessScheduleType.Schedule

  if (isTemporary || isRecurring) {
    fromDate = isTemporary ? temporaryFromDate : scheduleFromDate
    toDate = isTemporary ? temporaryToDate : scheduleToDate

    if (isRecurring && scheduleAllDay) {
      const { startDate, endDate } = getReferenceScheduleDates({
        frequency,
        daysOfWeek,
      })

      fromDate = combineDateAndTime(startDate, null)
      toDate = addDays(combineDateAndTime(endDate, null), 1)
    }

    if (!fromDate || !toDate) return null

    if (isRecurring && daysOfWeek.length > 0 && frequency !== Frequency.Daily) {
      const alignedRange = alignRangeToFirstWeekday(
        fromDate,
        toDate,
        daysOfWeek
      )
      fromDate = alignedRange.fromDate
      toDate = alignedRange.toDate
    }

    const startDate = roundToWholeMinutes(fromDate)
    const endDate = roundToWholeMinutes(toDate)

    const durationMs = isRecurring
      ? getRecurringScheduleDurationMs(
          startDate,
          endDate,
          frequency,
          daysOfWeek
        )
      : endDate.getTime() - startDate.getTime()

    let recurrenceFrequency: Frequency | undefined = undefined
    let repeatOnWeekdays: DateAdapter.Weekday[] | undefined = undefined
    let repeatUntilDate: Date | undefined = undefined
    let recurrenceRules: RRule[] | undefined = undefined

    if (repeatUntil) {
      repeatUntilDate = roundToWholeMinutes(repeatUntil)
    }

    if (
      isRecurring &&
      frequency === Frequency.SelectedDays &&
      daysOfWeek.length
    ) {
      repeatOnWeekdays = sortWeekdays(daysOfWeek) as DateAdapter.Weekday[]
    }

    if (isRecurring) {
      recurrenceFrequency =
        frequency === Frequency.SelectedDays ? Frequency.Weekly : frequency
      const recurrenceRule = new RRule({
        start: startDate,
        end: repeatUntilDate,
        frequency: recurrenceFrequency || Frequency.Daily,
        ...(repeatOnWeekdays?.length ? { byDayOfWeek: repeatOnWeekdays } : {}),
      })
      recurrenceRules = [recurrenceRule]
    }

    const durationSeconds = Math.floor(durationMs / 1000)
    if (durationSeconds <= 0) return null

    const durationEnd = new Date(startDate.getTime() + durationSeconds * 1000)

    event = new VEvent({
      start: startDate,
      duration: durationEnd,
      rrules: recurrenceRules,
    })
  }

  const icalText = event?.toICal()
  return icalText ? applyTimezoneToIcalExport(icalText, timeZoneId) : null
}

/**
 * Adapter: płaska mapa pól profilu (accessScheduleType, …) → iCal z domyślną strefą.
 */
export const scheduleFormValuesToIcal = ({
  accessScheduleType,
  temporaryFromDate,
  temporaryToDate,
  scheduleFromDate,
  scheduleToDate,
  repeatUntil,
  frequency,
  daysOfWeek,
  scheduleAllDay = false,
  timeZone,
}: {
  accessScheduleType: AccessScheduleType
  temporaryFromDate: Date | null
  temporaryToDate: Date | null
  scheduleFromDate: Date | null
  scheduleToDate: Date | null
  repeatUntil: Date | null
  frequency: Frequency
  daysOfWeek: string[]
  scheduleAllDay?: boolean
  timeZone?: string
}): string | null =>
  scheduleFormToIcal(
    {
      scheduleType: accessScheduleType,
      temporaryFromDate,
      temporaryToDate,
      scheduleFromDate,
      scheduleToDate,
      repeatUntil,
      frequency,
      daysOfWeek,
      scheduleAllDay: scheduleAllDay ?? false,
    },
    {
      timeZoneId: (timeZone ?? DEFAULT_TIME_ZONE).trim(),
    }
  )

/** Parsuje VEVENT z powrotem na pola formularza harmonogramu. */
export function icalToScheduleForm(
  vEventString: string | null | undefined
): ParsedScheduleFromIcal {
  let recurringPatternLoadUnsupported = false
  let scheduleAllDay = false
  let scheduleType: AccessScheduleType = AccessScheduleType.Constant
  let temporaryFromDate: Date | null = null
  let temporaryToDate: Date | null = null
  let scheduleFromDate: Date | null = null
  let scheduleToDate: Date | null = null
  let repeatUntil: Date | null = null
  let daysOfWeek: string[] = []
  let frequency: Frequency = Frequency.Daily

  if (vEventString?.trim()) {
    if (
      /(?:^|\n)RRULE:/i.test(flattenIcalLines(vEventString)) &&
      !isRecurringPatternSupportedInUi(vEventString)
    ) {
      recurringPatternLoadUnsupported = true
    }

    const event = VEvent.fromICal(removeTimezoneFromIcalDates(vEventString))[0]
    const recurrenceOptions = event?.rrules?.[0]?.options || null
    const eventFrequency = recurrenceOptions?.frequency
      ? (String(recurrenceOptions.frequency).toUpperCase() as Exclude<
          Frequency,
          Frequency.SelectedDays
        >)
      : null

    const startDate = event?.start ? new Date(event.start.toISOString()) : null
    const endDate = event?.data?.jCal?.[1]?.[1]?.[3]
      ? new Date(event.data.jCal[1][1][3])
      : null

    if (eventFrequency !== null) {
      scheduleType = AccessScheduleType.Schedule
      scheduleFromDate = startDate
      scheduleToDate = endDate

      const untilRaw = event?.data?.jCal?.[1]?.[2]?.[3]?.until as string
      repeatUntil = untilRaw ? new Date(untilRaw) : null

      const weekdaysFromIcal = weekdayCodesFromIcal(vEventString)
      const rruleLine = flattenIcalLines(vEventString || '').match(
        /(?:^|\n)RRULE:[^\n]+/i
      )?.[0]
      const hasByDayInRule = !!(rruleLine && /\bBYDAY=/i.test(rruleLine))

      daysOfWeek =
        weekdaysFromIcal ??
        (recurrenceOptions?.byDayOfWeek?.length
          ? sortWeekdays([...(recurrenceOptions.byDayOfWeek as string[])])
          : eventFrequency === Frequency.Weekly
          ? weekdaysFromWeeklyRecurringRange(startDate, endDate)
          : weekdaysBetweenDates(startDate, endDate))

      if (eventFrequency === Frequency.Weekly) {
        frequency = hasByDayInRule ? Frequency.SelectedDays : Frequency.Weekly
      } else {
        frequency = eventFrequency as Frequency
      }

      if (frequency === Frequency.Daily && !hasByDayInRule) {
        daysOfWeek = []
      }

      scheduleAllDay = resolveScheduleAllDayFromIcal(
        startDate,
        endDate,
        frequency
      )

      if (scheduleFromDate && scheduleToDate && !scheduleAllDay) {
        const normalized = normalizeScheduleFormDates(
          scheduleFromDate,
          scheduleToDate,
          frequency,
          daysOfWeek
        )
        scheduleFromDate = normalized.scheduleFromDate
        scheduleToDate = normalized.scheduleToDate
      }
    } else {
      scheduleType = AccessScheduleType.Temporary
      temporaryFromDate = startDate
      temporaryToDate = endDate
    }
  }

  if (
    recurringPatternLoadUnsupported &&
    scheduleType === AccessScheduleType.Schedule
  ) {
    frequency = Frequency.Daily
    daysOfWeek = []
    scheduleAllDay = false
  }

  return {
    scheduleType,
    temporaryFromDate,
    temporaryToDate,
    scheduleFromDate,
    scheduleToDate,
    repeatUntil,
    frequency,
    daysOfWeek,
    scheduleAllDay,
    recurringPatternLoadUnsupported,
  }
}
